import { cloneDeep, differenceWith, find, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  CreateProductPreferencesAction,
  DeleteProductPreferencesAction,
  FetchProductPreferencesAction,
  ProductPreferences,
  ReduxStoreData,
  ServerProductPreferencesCreate,
  ServerProductPreferencesUpdate,
  UpdateProductPreferencesAction,
  UpsertProductPreferencesAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { waitForAuth } from '../_common';
import {
  createProductPreferences,
  createProductPreferencesFail,
  createProductPreferencesSuccess,
  deleteProductPreferencesFail,
  deleteProductPreferencesSuccess,
  fetchProductPreferencesFail,
  fetchProductPreferencesSuccess,
  updateProductPreferences,
  updateProductPreferencesFail,
  updateProductPreferencesSuccess,
  upsertProductPreferencesFail,
  upsertProductPreferencesSuccess,
} from './actions';
import ProductPreferencesAPI from './api';
import { getProductPreferencesId } from './utils';

export function* createProductPreferencesWorker(action: CreateProductPreferencesAction): any {
  try {
    yield waitForAuth();
    const {
      applicationId,
      productPreferences: { clientId },
    } = action;

    const productPreference = omit(action.productPreferences, [
      'clientId',
      'applicationId',
      'applicantId',
    ]) as ServerProductPreferencesCreate;

    const response = yield call(ProductPreferencesAPI.create, applicationId, productPreference);

    yield put(createProductPreferencesSuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      productPreferences: { clientId },
    } = action;
    yield put(createProductPreferencesFail(clientId, e.response?.data || e.message));
  }
}

export function* deleteProductPreferencesWorker(action: DeleteProductPreferencesAction) {
  try {
    yield waitForAuth();
    const { applicationId, clientId } = action;

    yield call(ProductPreferencesAPI.del, applicationId, clientId);

    yield put(deleteProductPreferencesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield put(deleteProductPreferencesFail(clientId, e.response?.data || e.message));
  }
}

export function* fetchProductPreferencesWorker(action: FetchProductPreferencesAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;

    let response;
    if (applicationId) {
      response = yield call(ProductPreferencesAPI.fetch, applicationId);
    } else {
      response = yield call(ProductPreferencesAPI.fetchAll, applicationId);
    }

    const productPreferences = response.data.map((productPreference: any) =>
      omit(productPreference, ['meta', 'updatedAt'])
    );
    yield put(fetchProductPreferencesSuccess(productPreferences));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(fetchProductPreferencesFail(e.response?.data || e.message));
  }
}

export function* updateProductPreferencesWorker(action: UpdateProductPreferencesAction): any {
  try {
    yield waitForAuth();
    const { applicationId, productPreferences } = action;
    const productPreferenceId = yield getProductPreferencesId(productPreferences.clientId, productPreferences.id);

    const productPreference = omit(productPreferences, [
      'id',
      'clientId',
      'createdAt',
      'isDeidentified',
      'isDeleted',
      'applicationId',
      'applicantId',
    ]) as ServerProductPreferencesUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!productPreferenceId) {
      yield put(createProductPreferences(applicationId, action.productPreferences));
    } else {
      yield call(ProductPreferencesAPI.update, applicationId, productPreference, productPreferenceId);

      yield put(updateProductPreferencesSuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      productPreferences: { clientId },
    } = action;
    yield put(updateProductPreferencesFail(clientId, e.response?.data || e.message));
  }
}

export function* upsertProductPreferencesWorker(action: UpsertProductPreferencesAction) {
  try {
    yield waitForAuth();
    const { applicationId, productPreferences } = action;

    const { productPreferences: reduxStoreProductPreferences }: { productPreferences: ProductPreferences[] } =
      yield select((state: ReduxStoreData) => ({
        productPreferences: state.productPreferences.data,
      }));

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedProductPreferences = differenceWith(
      productPreferences,
      reduxStoreProductPreferences,
      ({ id: id1, ...rest1 }, { id: id2, ...rest2 }) => isEqual(rest1, rest2)
    );

    yield all(
      cloneDeep(changedProductPreferences).map((clientProductPreferences) => {
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const foundReduxStoreProductPreferences = find(reduxStoreProductPreferences, {
          clientId: clientProductPreferences.clientId,
        }) as ProductPreferences;

        if (!foundReduxStoreProductPreferences) {
          // No productPreference
          return put(createProductPreferences(applicationId, clientProductPreferences));
        } else if (!foundReduxStoreProductPreferences.id) {
          // productPreference but no id, therefore create has been fired but success hasn't returned
          return put(updateProductPreferences(applicationId, clientProductPreferences));
        }

        // ProductPreferences is in the store and has an id
        return put(
          updateProductPreferences(applicationId, {
            id: foundReduxStoreProductPreferences.id,
            ...clientProductPreferences,
          })
        );
      })
    );
    yield put(upsertProductPreferencesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(upsertProductPreferencesFail(e.response?.data || e.message));
  }
}

export function* productPreferencesWatcher() {
  yield takeEvery(
    REDUX_STORE.PRODUCT_PREFERENCES.ACTION_TYPES.CREATE_PRODUCT_PREFERENCES,
    createProductPreferencesWorker
  );
  yield takeEvery(
    REDUX_STORE.PRODUCT_PREFERENCES.ACTION_TYPES.DELETE_PRODUCT_PREFERENCES,
    deleteProductPreferencesWorker
  );
  yield takeEvery(
    REDUX_STORE.PRODUCT_PREFERENCES.ACTION_TYPES.FETCH_PRODUCT_PREFERENCES,
    fetchProductPreferencesWorker
  );
  yield takeEvery(
    REDUX_STORE.PRODUCT_PREFERENCES.ACTION_TYPES.UPDATE_PRODUCT_PREFERENCES,
    updateProductPreferencesWorker
  );
  yield takeEvery(
    REDUX_STORE.PRODUCT_PREFERENCES.ACTION_TYPES.UPSERT_PRODUCT_PREFERENCES,
    upsertProductPreferencesWorker
  );
}

export default productPreferencesWatcher;
